import type { Membership } from '../../../lib/api';

/**
 * Per-tier `maintenance_cashback` editor. Each tier stores
 *   config.computation.maintenance_cashback = { percentage, min_ppv }
 * The member earns `percentage`% of their OWN range PPV back as cash, but only
 * if that PPV ≥ `min_ppv` (maintenance). One row per tier, two scalar columns.
 */
type Block = Record<string, unknown>;

const SCALARS = [
  ['percentage', 'Cashback %'],
  ['min_ppv', 'Min PPV'],
] as const;

export function MaintenanceCashbackPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  function block(cfg: Record<string, unknown>): Block {
    const computation = (cfg.computation as Block | undefined) ?? {};
    return (computation.maintenance_cashback as Block | undefined) ?? {};
  }

  function writeBlock(id: string, nextBlock: Block) {
    const current = draft[id] ?? {};
    const computation = { ...((current.computation as Block | undefined) ?? {}) };
    const cleaned: Block = {};
    for (const [k, v] of Object.entries(nextBlock)) {
      if (v === undefined || v === 0) continue;
      cleaned[k] = v;
    }
    if (Object.keys(cleaned).length === 0) delete computation.maintenance_cashback;
    else computation.maintenance_cashback = cleaned;
    const next = { ...current };
    if (Object.keys(computation).length === 0) delete next.computation;
    else next.computation = computation;
    onChange(id, next);
  }

  function getScalar(cfg: Record<string, unknown>, key: string): number {
    const v = block(cfg)[key];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }
  function setScalar(id: string, key: string, n: number) {
    const b = { ...block(draft[id] ?? {}) };
    if (n === 0) delete b[key];
    else b[key] = n;
    writeBlock(id, b);
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground">
        Each tier earns <strong>Cashback %</strong> of its own range PPV back as
        cash, paid to the member themselves — but only when their PPV for the run
        window reaches <strong>Min PPV</strong> (maintenance). Run as an admin
        date-range distribution. Blank / 0 = no cashback for that tier.
      </p>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Tier</th>
              {SCALARS.map(([key, label]) => (
                <th key={key} className="px-3 py-2 font-medium">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {memberships.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="px-3 py-1.5 text-xs font-mono" title={m.name}>{m.code}</td>
                {SCALARS.map(([key]) => (
                  <td key={key} className="px-2 py-1.5">
                    <input
                      type="number"
                      className="h-8 w-24 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                      value={getScalar(draft[m.id] ?? {}, key)}
                      disabled={readOnly}
                      step={key === 'percentage' ? 0.01 : 1}
                      min={0}
                      onChange={(e) => setScalar(m.id, key, Number(e.target.value) || 0)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
